// HUB-UI: customer hub (Hub design 06). Each section owns its API load and failure state.
import { useCallback, useEffect, useState } from 'react';
import { useSharedPreferences } from '../../shared/preferences';
import { Link, useSearchParams } from 'react-router-dom';
import { Alert, Box, Button, Chip, MenuItem, Stack, TextField, Typography } from '@mui/material';
import ArrowForward from '@mui/icons-material/ArrowForward';
import FileDownloadOutlined from '@mui/icons-material/FileDownloadOutlined';
import AddLocationAltOutlined from '@mui/icons-material/AddLocationAltOutlined';
import KeyOutlined from '@mui/icons-material/KeyOutlined';
import ShoppingBagOutlined from '@mui/icons-material/ShoppingBagOutlined';
import WorkOutline from '@mui/icons-material/WorkOutline';
import FavoriteBorder from '@mui/icons-material/FavoriteBorder';
import {
  hubApi,
  read,
  recordUrl,
  write,
  type Address,
  type CustomerRecord,
  type Preferences,
  type Values,
} from './hubApi';
import {
  EntityDialog,
  EntityGrid,
  errorMessage,
  Fields,
  HubEmpty,
  HubPager,
  HubSection,
  HubStat,
  HubTitle,
  LoadState,
  useHubLoad,
  type Field,
} from './HubUi';
import { OrganizationCardView } from '../public/components';
import { ProfilePage } from '../profile/ProfilePage';
import { SessionsPage } from '../profile/SessionsPage';
import { savedDestination } from '../auth/DestinationPicker';
import { useAppSelector } from '../../app/store';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { ApplyDialog } from './ApplyDialog';

function RecordsGrid({ records }: { records: CustomerRecord[] }) {
  return (
    <EntityGrid
      rows={records}
      rowKey={(row) => row.id}
      columns={[
        {
          label: 'Activity',
          render: (row) => (
            <div>
              <strong>{row.title}</strong>
              {row.subtitle && (
                <Typography variant="body2" color="text.secondary">
                  {row.subtitle}
                </Typography>
              )}
            </div>
          ),
        },
        { label: 'Company / product', render: (row) => `${row.organizationName} · ${row.productCode}` },
        { label: 'When', render: (row) => new Date(row.occursAt).toLocaleString() },
        { label: 'Status', render: (row) => <Chip size="small" label={row.status} /> },
        { label: 'Amount', render: (row) => (row.amount === null ? '—' : `${row.amount} ${row.currencyCode ?? ''}`) },
      ]}
      actions={(row) => {
        const url = recordUrl(row);
        return (
          url && (
            <Button component="a" href={url} size="small" endIcon={<ArrowForward />}>
              Open details
            </Button>
          )
        );
      }}
    />
  );
}
export function CustomerOverviewPage() {
  const load = useHubLoad(useCallback((signal: AbortSignal) => hubApi.overview(signal), []), true);
  useEffect(() => {
    const timer = window.setInterval(() => { if (!document.hidden) load.reload(); }, 15000);
    const refresh = () => load.reload();
    window.addEventListener('focus', refresh);
    return () => { window.clearInterval(timer); window.removeEventListener('focus', refresh); };
  }, [load.reload]);
  const profile = useAppSelector((state) => state.session.profile);
  const next = load.data?.nextReservation ?? null;
  const nextUrl = next ? recordUrl(next) : null;
  return (
    <div className="hub-overview">
      <header className="hub-greeting">
        <h1>Hello, {profile.firstName}</h1>
        <span className="hub-greeting-date">
          {new Date().toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' })}
        </span>
      </header>
      <LoadState {...load} />
      <div className="hub-stats hub-rise">
        <HubStat
          icon={ShoppingBagOutlined}
          label="Active orders"
          value={load.data ? load.data.activeOrders.length : '-'}
          to="/me/activity"
        />
        <HubStat icon={WorkOutline} label="Applications" value={load.data ? load.data.applications : '-'} to="/me/applications" />
        <HubStat icon={FavoriteBorder} label="Favourites" value={load.data ? load.data.favourites : '-'} to="/me/favourites" />
      </div>
      {load.data && (
        <>
          <HubSection title="Next reservation">
            {next ? (
              <article className="hub-next">
                <span className="hub-next-date" aria-hidden>
                  <span>{new Date(next.occursAt).toLocaleDateString(undefined, { month: 'short' })}</span>
                  <strong>{new Date(next.occursAt).getDate()}</strong>
                </span>
                <div className="hub-next-main">
                  <strong>{next.title}</strong>
                  <span>
                    {next.organizationName} ·{' '}
                    {new Date(next.occursAt).toLocaleString(undefined, { weekday: 'short', hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
                <Chip size="small" label={next.status} />
                {nextUrl && (
                  <Button component="a" href={nextUrl} size="small" endIcon={<ArrowForward />}>
                    Open details
                  </Button>
                )}
              </article>
            ) : (
              <HubEmpty
                action={
                  <Button component={Link} to="/search" variant="contained" disableElevation>
                    Explore companies
                  </Button>
                }
              >
                No upcoming reservations.
              </HubEmpty>
            )}
          </HubSection>
          {load.data.activeOrders.length > 0 && (
            <HubSection title="Active orders">
              <RecordsGrid records={load.data.activeOrders} />
            </HubSection>
          )}
        </>
      )}
    </div>
  );
}
export function CustomerActivityPage() {
  const [status, setStatus] = useState('Upcoming');
  const [type, setType] = useState('');
  const [product, setProduct] = useState('');
  const [page, setPage] = useState(1);
  const load = useHubLoad(
    useCallback(
      (signal: AbortSignal) => hubApi.records({ status, type, product, page, pageSize: 20 }, signal),
      [status, type, product, page],
    ),
  );
  return (
    <>
      <HubTitle title="Reservations & orders" />
      <div className="hub-toolbar" role="group" aria-label="Filters">
        <TextField
          select
          label="Status"
          value={status}
          onChange={(event) => {
            setStatus(event.target.value);
            setPage(1);
          }}
        >
          {['Upcoming', 'Active', 'Completed', 'Cancelled'].map((value) => (
            <MenuItem key={value} value={value}>
              {value === 'Completed' ? 'Past' : value}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Type"
          value={type}
          onChange={(event) => {
            setType(event.target.value);
            setPage(1);
          }}
        >
          <MenuItem value="">All</MenuItem>
          {['Reservation', 'Order'].map((value) => (
            <MenuItem key={value} value={value}>
              {value}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Product"
          value={product}
          onChange={(event) => {
            setProduct(event.target.value);
            setPage(1);
          }}
        />
      </div>
      <LoadState {...load} />
      {load.data && (
        <>
          <RecordsGrid records={load.data.items} />
          <HubPager page={page} pageSize={load.data.pageSize} total={load.data.total} onPage={setPage} />
        </>
      )}
    </>
  );
}
export function CustomerTimelinePage() {
  const [year, setYear] = useState(new Date().getFullYear());
  const load = useHubLoad(useCallback((signal: AbortSignal) => hubApi.timeline(year, signal), [year]));
  return (
    <>
      <HubTitle title="Your timeline" />
      <div className="hub-toolbar">
        <TextField
          type="number"
          label="Year"
          value={year}
          inputProps={{ min: 2000, max: 2100, inputMode: 'numeric' }}
          onChange={(event) => {
            const value = Number(event.target.value);
            if (value >= 2000 && value <= 2100) setYear(value);
          }}
        />
      </div>
      <LoadState {...load} />
      {load.data && (
        <>
          {load.data.totals.length > 0 && (
            <div className="hub-summary-grid">
              {load.data.totals.map((total) => (
                <div key={total.currencyCode} className="hub-summary">
                  <span>Total · {total.currencyCode}</span>
                  <strong>{total.amount}</strong>
                </div>
              ))}
            </div>
          )}
          {load.data.months.length === 0 && <Alert severity="info">No activity in this year.</Alert>}
          {load.data.months.map((month) => (
            <HubSection
              key={month.month}
              title={new Date(year, month.month - 1).toLocaleString(undefined, { month: 'long', year: 'numeric' })}
            >
              <RecordsGrid records={month.records} />
            </HubSection>
          ))}
        </>
      )}
    </>
  );
}
const addressFields: Field[] = [
  { key: 'label', label: 'Label', maxLength: 50 },
  { key: 'recipientName', label: 'Recipient', required: true, maxLength: 200 },
  { key: 'phone', label: 'Phone', required: true, maxLength: 50 },
  { key: 'addressLine', label: 'Address', required: true, maxLength: 300 },
  { key: 'city', label: 'City', required: true, maxLength: 100 },
  { key: 'postalCode', label: 'Postal code', maxLength: 20 },
  {
    key: 'countryCode',
    label: 'Country code',
    required: true,
    maxLength: 2,
    helper: 'Two-letter country code, e.g. AL, DE, IT',
  },
  { key: 'latitude', label: 'Latitude', type: 'number', min: -90, max: 90 },
  { key: 'longitude', label: 'Longitude', type: 'number', min: -180, max: 180 },
  { key: 'isDefault', label: 'Default address', type: 'boolean' },
];
export function CustomerAddressesPage() {
  const load = useHubLoad(useCallback((signal: AbortSignal) => hubApi.addresses(signal), []));
  const [edit, setEdit] = useState<Values | null>(null);
  const [remove, setRemove] = useState<Address | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <>
      <HubTitle
        title="Addresses"
        action={
          <Button
            variant="contained"
            startIcon={<AddLocationAltOutlined />}
            disabled={!load.data}
            onClick={() =>
              setEdit({ recipientName: '', phone: '', addressLine: '', city: '', countryCode: 'AL', isDefault: false })
            }
          >
            Add address
          </Button>
        }
      />
      <LoadState {...load} />
      {error && <Alert severity="error">{error}</Alert>}
      {load.data && (
        <EntityGrid
          rows={load.data}
          rowKey={(row) => row.id}
          columns={[
            { label: 'Recipient', render: (row) => row.recipientName },
            { label: 'Address', render: (row) => `${row.addressLine}, ${row.city}, ${row.countryCode}` },
            { label: 'Phone', render: (row) => row.phone },
            {
              label: 'Default',
              render: (row) => (row.isDefault ? <Chip size="small" color="primary" label="Default" /> : 'No'),
            },
          ]}
          actions={(row) => (
            <>
              <Button size="small" onClick={() => setEdit(row)}>
                Edit
              </Button>
              <Button size="small" color="error" onClick={() => setRemove(row)}>
                Delete
              </Button>
            </>
          )}
        />
      )}
      {edit && (
        <EntityDialog
          title={edit.id ? 'Edit address' : 'Add address'}
          value={edit}
          fields={addressFields}
          close={() => setEdit(null)}
          validate={(value) => (String(value.countryCode).length !== 2 ? 'Use a two-letter country code.' : null)}
          save={async (value) => {
            const body = Object.fromEntries(addressFields.map((field) => [field.key, value[field.key] ?? null]));
            body.countryCode = String(body.countryCode).toUpperCase();
            await write(`/me/addresses${edit.id ? `/${edit.id}` : ''}`, body, edit.id ? 'put' : 'post');
            load.reload();
          }}
        />
      )}
      <ConfirmDialog
        open={!!remove}
        title="Delete address?"
        message="This address will be removed from your account."
        busy={busy}
        onClose={() => setRemove(null)}
        onConfirm={async () => {
          if (!remove) return;
          setBusy(true);
          setError(null);
          try {
            await write(`/me/addresses/${remove.id}`, null, 'delete');
            setRemove(null);
            load.reload();
          } catch (caught) {
            setError(errorMessage(caught));
          } finally {
            setBusy(false);
          }
        }}
      />
    </>
  );
}
export function CustomerFavouritesPage() {
  const load = useHubLoad(useCallback((signal: AbortSignal) => hubApi.favourites(signal), []));
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  return (
    <>
      <HubTitle title="Favourite companies" />
      <LoadState {...load} />
      {error && <Alert severity="error">{error}</Alert>}
      {load.data?.length === 0 && (
        <Alert severity="info">
          No favourites yet. <Link to="/search">Find a company</Link>
        </Alert>
      )}
      {load.data && (
        <div className="hub-grid">
          {load.data.map((card) => (
            <div key={card.id} className="hub-favourite">
              <OrganizationCardView card={card} />
              <Button
                size="small"
                color="inherit"
                disabled={busy !== null}
                onClick={async () => {
                  setBusy(card.id);
                  setError(null);
                  try {
                    await write(`/me/favourites/${card.organizationId}`, null, 'delete');
                    load.reload();
                  } catch (caught) {
                    setError(errorMessage(caught));
                  } finally {
                    setBusy(null);
                  }
                }}
              >
                {busy === card.id ? 'Removing…' : 'Remove favourite'}
              </Button>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
export function CustomerApplicationsPage() {
  const [params, setParams] = useSearchParams();
  const load = useHubLoad(useCallback((signal: AbortSignal) => hubApi.applications(signal), []));
  const [pending, setPending] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <>
      <HubTitle title="Job applications" />
      <LoadState {...load} />
      {error && <Alert severity="error">{error}</Alert>}
      {load.data && (
        <EntityGrid
          rows={load.data}
          rowKey={(row) => row.id}
          columns={[
            { label: 'Position', render: (row) => <Link to={`/jobs/${row.jobId}`}>{row.title}</Link> },
            { label: 'Company', render: (row) => row.organizationName },
            { label: 'Stage', render: (row) => <Chip size="small" label={row.stage} /> },
            { label: 'Submitted', render: (row) => new Date(row.submittedAt).toLocaleDateString() },
          ]}
          actions={(row) =>
            !['Withdrawn', 'Hired', 'Rejected'].includes(row.stage) && (
              <Button size="small" color="error" onClick={() => setPending(row.id)}>
                Withdraw
              </Button>
            )
          }
        />
      )}
      <ConfirmDialog
        open={!!pending}
        title="Withdraw application?"
        message="The company will see that you withdrew."
        busy={busy}
        onClose={() => setPending(null)}
        onConfirm={async () => {
          if (!pending) return;
          setBusy(true);
          setError(null);
          try {
            await write(`/me/applications/${pending}/withdraw`, {}, 'post');
            setPending(null);
            load.reload();
          } catch (caught) {
            setError(errorMessage(caught));
          } finally {
            setBusy(false);
          }
        }}
      />
      {params.get('jobId') && (
        <ApplyDialog
          jobId={params.get('jobId')!}
          close={() => {
            setParams({});
            load.reload();
          }}
        />
      )}
    </>
  );
}
const preferenceFields: Field[] = [
  {
    key: 'language',
    label: 'Language',
    type: 'select',
    options: ['al', 'en', 'it'].map((value) => ({ value, label: value.toUpperCase() })),
  },
  ...[
    ['emailEnabled', 'Email notifications'],
    ['smsEnabled', 'SMS notifications'],
    ['remindersEnabled', 'Reservation reminders'],
    ['orderUpdatesEnabled', 'Order updates'],
    ['applicationUpdatesEnabled', 'Application updates'],
    ['marketingEnabled', 'Marketing messages'],
  ].map(([key, label]) => ({ key, label, type: 'boolean' as const })),
];
function PreferencesForm({ initial, reload }: { initial: Preferences; reload: () => void }) {
  const shared = useSharedPreferences();
  const [form, setForm] = useState<Values>({ ...initial, language: shared.language });
  useEffect(() => { setForm(value => ({ ...value, language: shared.language })); }, [shared.language]);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  return (
    <Box
      component="form"
      onSubmit={async (event) => {
        event.preventDefault();
        setBusy(true);
        setError(null);
        setMessage(null);
        try {
          await write('/me/preferences', form);
          shared.setLanguage(String(form.language));
          setMessage('Preferences saved.');
          reload();
        } catch (caught) {
          setError(errorMessage(caught));
        } finally {
          setBusy(false);
        }
      }}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {message && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {message}
        </Alert>
      )}
      <Box className="hub-panel">
        <Fields fields={preferenceFields} value={form} onChange={setForm} disabled={busy} />
      </Box>
      <Button sx={{ mt: 3 }} variant="contained" type="submit" disabled={busy}>
        {busy ? 'Saving…' : 'Save preferences'}
      </Button>
    </Box>
  );
}
export function CustomerNotificationsPage() {
  const load = useHubLoad(useCallback((signal: AbortSignal) => hubApi.preferences(signal), []));
  return (
    <>
      <HubTitle title="Notification settings" />
      <LoadState {...load} />
      {load.data && <PreferencesForm initial={load.data} reload={() => undefined} />}
    </>
  );
}
export function CustomerAccountPage() {
  const login = useAppSelector((state) => state.session.profile.email);
  const load = useHubLoad(useCallback((signal: AbortSignal) => hubApi.preferences(signal), []));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <>
      <HubTitle title="Account & security" />
      {/* HUB-UI: account includes existing identity editor and session revocation. */}
      <ProfilePage embedded />
      <HubSection title="Default destination" subtitle="Where Architech opens after you sign in.">
        <LoadState {...load} />
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {load.data && (
          <Box className="hub-panel" sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <Typography sx={{ fontWeight: 600 }}>{load.data.defaultDestinationCode ?? 'Ask me each time'}</Typography>
            <Button
              variant="outlined"
              disabled={busy}
              onClick={async () => {
                if (!load.data) return;
                setBusy(true);
                setError(null);
                try {
                  await write('/me/preferences', {
                    ...load.data,
                    defaultOrganizationId: null,
                    defaultDestinationCode: null,
                  });
                  savedDestination.clear(login);
                  load.reload();
                } catch (caught) {
                  setError(errorMessage(caught));
                } finally {
                  setBusy(false);
                }
              }}
            >
              Forget my choice
            </Button>
          </Box>
        )}
      </HubSection>
      <HubSection title="Password" subtitle="Use a password you do not use anywhere else.">
        <PasswordForm />
      </HubSection>
      <section className="hub-block">
        <SessionsPage embedded />
      </section>
    </>
  );
}
// HUB-UI: use existing password endpoint, requiring the current password for self-service.
function PasswordForm() {
  const profile = useAppSelector((state) => state.session.profile);
  const [edit, setEdit] = useState(false);
  return (
    <Box className="hub-panel">
      {edit ? (
        <PasswordDialog userId={profile.id} close={() => setEdit(false)} />
      ) : (
        <Button variant="outlined" startIcon={<KeyOutlined />} onClick={() => setEdit(true)}>
          Change password
        </Button>
      )}
    </Box>
  );
}
function PasswordDialog({ userId, close }: { userId: string; close: () => void }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <Box
      component="form"
      onSubmit={async (event) => {
        event.preventDefault();
        if (next !== confirm) {
          setError('Passwords do not match.');
          return;
        }
        setBusy(true);
        setError(null);
        try {
          await write(
            '/organization/memberships/change-password',
            { userId, oldPassword: current, newPassword: next, confirmPassword: confirm },
            'post',
          );
          close();
        } catch (caught) {
          setError(errorMessage(caught));
        } finally {
          setBusy(false);
        }
      }}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Stack gap={2} sx={{ mb: 2, maxWidth: 520 }}>
        <TextField
          type="password"
          label="Current password"
          required
          value={current}
          onChange={(event) => setCurrent(event.target.value)}
          autoComplete="current-password"
          disabled={busy}
        />
        <TextField
          type="password"
          label="New password"
          required
          value={next}
          onChange={(event) => setNext(event.target.value)}
          autoComplete="new-password"
          disabled={busy}
        />
        <TextField
          type="password"
          label="Confirm password"
          required
          value={confirm}
          onChange={(event) => setConfirm(event.target.value)}
          autoComplete="new-password"
          disabled={busy}
        />
      </Stack>
      <Stack direction="row" gap={1} flexWrap="wrap">
        <Button type="submit" variant="contained" disabled={busy}>
          {busy ? 'Saving…' : 'Save password'}
        </Button>
        <Button onClick={close} disabled={busy} color="inherit">
          Cancel
        </Button>
      </Stack>
    </Box>
  );
}
export function CustomerPrivacyPage() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <>
      <HubTitle
        title="Privacy"
        subtitle="Export your Platform profile and activity. Product-specific data is managed by each product."
      />
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Box className="hub-panel" sx={{ display: 'grid', gap: 2, justifyItems: 'start', maxWidth: 640 }}>
        <Typography color="text.secondary">
          Downloads your Platform data as a JSON file.
        </Typography>
      <Button
        variant="contained"
        startIcon={<FileDownloadOutlined />}
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          setError(null);
          try {
            const data = await read<unknown>('/me/privacy/export');
            const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }));
            const link = document.createElement('a');
            link.href = url;
            link.download = 'architech-my-data.json';
            link.click();
            window.setTimeout(() => URL.revokeObjectURL(url), 1000);
          } catch (caught) {
            setError(errorMessage(caught));
          } finally {
            setBusy(false);
          }
        }}
      >
        {busy ? 'Preparing export…' : 'Export my data'}
      </Button>
      </Box>
    </>
  );
}
